import { useState, useEffect } from "react";
import { motion } from "framer-motion";
import { Target, Image, Camera, Check, X } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/contexts/AuthContext";

interface TourStep {
  icon: React.ElementType;
  label: string;
  description: string;
  route: string;
  checkTable: string;
}

const STEPS: TourStep[] = [
  {
    icon: Target,
    label: "Set a goal",
    description: "Create your first shared goal together",
    route: "/app/goals",
    checkTable: "goals",
  },
  {
    icon: Image,
    label: "Add a vision board item",
    description: "Add an affirmation or image to your vision",
    route: "/app/vision",
    checkTable: "vision_items",
  },
  {
    icon: Camera,
    label: "Upload a memory",
    description: "Share a photo that means something to you both",
    route: "/app",
    checkTable: "gallery_photos",
  },
];

export default function ProductTour() {
  const { profile } = useAuth();
  const navigate = useNavigate();
  const [completed, setCompleted] = useState<Record<string, boolean>>({});
  const [dismissed, setDismissed] = useState(false);

  const workspaceId = profile?.workspace_id;

  useEffect(() => {
    if (!workspaceId) return;

    const checkAll = async () => {
      const results: Record<string, boolean> = {};
      for (const step of STEPS) {
        const { count } = await supabase
          .from(step.checkTable as any)
          .select("id", { count: "exact", head: true })
          .eq("workspace_id", workspaceId);
        results[step.checkTable] = (count || 0) > 0;
      }
      setCompleted(results);
    };
    checkAll();
  }, [workspaceId]);

  const allDone = STEPS.every((s) => completed[s.checkTable]);

  if (dismissed || allDone) return null;

  return (
    <motion.div
      initial={{ opacity: 0, y: 12 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.6, delay: 0.2 }}
      className="bg-card border border-border rounded-xl p-6 relative"
    >
      <button
        onClick={() => setDismissed(true)}
        className="absolute top-4 right-4 text-muted-foreground hover:text-foreground"
      >
        <X className="w-4 h-4" />
      </button>

      <h3 className="font-display text-lg font-semibold text-foreground mb-1">
        Getting started together
      </h3>
      <p className="text-sm text-muted-foreground mb-4">
        Complete these three steps to set up your shared space.
      </p>

      <div className="space-y-3">
        {STEPS.map((step) => {
          const done = completed[step.checkTable];
          return (
            <button
              key={step.checkTable}
              onClick={() => !done && navigate(step.route)}
              className={`w-full flex items-center gap-3 p-3 rounded-lg text-left transition-colors ${
                done
                  ? "bg-sage-light"
                  : "bg-secondary hover:bg-secondary/80 cursor-pointer"
              }`}
              disabled={done}
            >
              <div
                className={`w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0 ${
                  done ? "bg-primary" : "bg-muted"
                }`}
              >
                {done ? (
                  <Check className="w-4 h-4 text-primary-foreground" />
                ) : (
                  <step.icon className="w-4 h-4 text-muted-foreground" />
                )}
              </div>
              <div>
                <p
                  className={`text-sm font-medium ${
                    done ? "text-primary line-through" : "text-foreground"
                  }`}
                >
                  {step.label}
                </p>
                <p className="text-xs text-muted-foreground">{step.description}</p>
              </div>
            </button>
          );
        })}
      </div>
    </motion.div>
  );
}
